import { eventBus } from './EventBus.js';
import { diff } from './diff.js';

export class Component {
    constructor(props = {}) {
        this.props = props;
        this.state = {};
        this._vnode = null;           // Current rendered vnode tree
        this._el = null;              // Root DOM element
        this._parentVnode = null;     // Parent vnode reference
        this._cleanupRegistry = new Set();
        this._boundHandlers = new Map();
        this._context = new Map();
        this._parent = null;
    }

    /**
     * Update component state and trigger re-render
     * @param {Object} newState - State to merge
     */
    setState(newState) {
        const oldState = { ...this.state };
        this.state = { ...this.state, ...newState };

        if (this.shouldUpdate(this.props, this.props, oldState, this.state)) {
            this._update();
        }
    }

    /**
     * Determine if component should re-render
     * @param {Object} oldProps
     * @param {Object} newProps
     * @param {Object} oldState
     * @param {Object} newState
     * @returns {boolean}
     */
    shouldUpdate(oldProps, newProps, oldState, newState) {
        return true;
    }

    /**
     * Internal update method
     * @private
     */
    _update() {
        if (!this._el) return;

        // Skip update if not attached to DOM yet (e.g. setState called during didMount)
        const parent = this._el.parentNode;
        if (!parent) return;

        this.willUpdate?.();

        const oldVnode = this._vnode;
        const newVnode = this.render();

        const index = Array.from(parent.childNodes).indexOf(this._el);
        diff(parent, oldVnode, newVnode, index);
        this._el = parent.childNodes[index];
        this._vnode = newVnode;

        this.didUpdate?.();
    }

    /**
     * Render method - must return vnode via h()
     * @returns {Object} vnode
     */
    render() {
        return null;
    }

    // Lifecycle hooks (override in subclasses)
    willMount() {}
    didMount() {}
    willUpdate() {}
    didUpdate() {}
    willUnmount() {}

    /**
     * Register cleanup function
     * @param {Function} fn - Cleanup function
     * @returns {Function} Unregister function
     */
    registerCleanup(fn) {
        if (typeof fn !== 'function') return () => {};
        this._cleanupRegistry.add(fn);
        return () => this._cleanupRegistry.delete(fn);
    }

    /**
     * Execute all cleanup functions
     * @private
     */
    _executeCleanup() {
        this._cleanupRegistry.forEach(fn => {
            try { fn(); } catch (e) { console.error('[Component] Cleanup error:', e); }
        });
        this._cleanupRegistry.clear();
    }

    /**
     * Auto-managed setTimeout
     */
    setTimeout(fn, ms) {
        const id = window.setTimeout(fn, ms);
        this.registerCleanup(() => window.clearTimeout(id));
        return id;
    }

    /**
     * Auto-managed setInterval
     */
    setInterval(fn, ms) {
        const id = window.setInterval(fn, ms);
        this.registerCleanup(() => window.clearInterval(id));
        return id;
    }

    /**
     * Subscribe to eventBus with auto-cleanup
     */
    subscribe(event, fn) {
        const unsub = eventBus.on(event, fn);
        this.registerCleanup(unsub);
        return unsub;
    }

    /**
     * Provide context value to children
     */
    provideContext(key, value) {
        this._context.set(key, value);
    }

    /**
     * Get context value from parent chain
     */
    getContext(key) {
        if (this._context.has(key)) {
            return this._context.get(key);
        }
        let parent = this._parent;
        while (parent) {
            if (parent._context?.has(key)) {
                return parent._context.get(key);
            }
            parent = parent._parent;
        }
        return undefined;
    }

    /**
     * Bind a handler function with caching
     * Returns the same bound function for the same handler across renders
     * This prevents unnecessary event listener updates in the diff
     * @param {Function} handler - The handler to bind
     * @returns {Function} Bound handler
     */
    bind(handler) {
        if (!this._boundHandlers.has(handler)) {
            this._boundHandlers.set(handler, handler.bind(this));
        }
        return this._boundHandlers.get(handler);
    }
}
